/**
 * TopToolbar - Hierarchical top toolbar replacing the sidebar.
 *
 * Navigation tree:
 *   root → play | edit | settings
 *   play → (future: inventory, character, map)
 *   edit → cube | npc | material | vegetation
 *   edit > cube → add / move / extend tools + swatch
 *   edit > npc → character picker + click-to-place
 *   edit > material → face picker active
 *   edit > vegetation → vegetation picker + click-to-place
 *
 * Implements SidebarManager-compatible API for backward compatibility.
 */
export default class TopToolbar {
  constructor(options = {}) {
    this.settingsPanel = options.settingsPanel || null;
    this.materialsSheet = options.materialsSheet || null;
    this.presetsSheet = options.presetsSheet || null;
    this.toolSheet = options.toolSheet || null;
    this.windowManager = options.windowManager || null;
    this.voxelEditor = null; // set via setVoxelEditor()
    this.npcTool = null; // set via setNPCTool()

    // Navigation stack — starts at root
    this._navStack = ['root'];

    // Track active tool for cube submenu highlight
    this._activeTool = null;

    // Backward compat: track which "tab" is logically active
    this.activeTab = 'game';

    // Hidden containers for content that used to go in sidebar tabs
    // These are off-screen divs that tools render into
    this.contentContainers = {};
    this._submenuContainers = {};

    this._createDOM();
    this._render();

    // Listen for NPC LPC config changes to re-render dropdown
    window.addEventListener('npc-lpc-config-changed', () => {
      if (this._currentLevel() === 'npc') {
        this._render();
      }
    });

    // Listen for NPC patrol state changes to update toolbar UI
    window.addEventListener('npc-patrol-update', () => {
      if (this._currentLevel() === 'npc') {
        this._updatePatrolUI();
      }
    });
  }

  // ---- DOM Creation ----

  _createDOM() {
    this.el = document.createElement('div');
    this.el.className = 'top-toolbar';

    // The content area (buttons swap based on nav state)
    this.contentEl = document.createElement('div');
    this.contentEl.className = 'toolbar-content';

    this.el.appendChild(this.contentEl);

    // Append to #container so everything shares the same stacking context
    const container = document.getElementById('container');
    (container || document.body).appendChild(this.el);

    // Clicking toolbar blurs focused windows/sidebar so toolbar stays on top
    this.el.addEventListener('pointerdown', () => {
      const c = this.el.parentNode;
      if (c) {
        c.querySelectorAll('.gw-window').forEach(w => {
          w.classList.remove('gw-focused');
          w.style.zIndex = '3000';
        });
        c.querySelectorAll('.gw-sidebar').forEach(s => {
          s.classList.remove('gw-focused');
          s.style.zIndex = '3500';
        });
      }
    }, { passive: true });

    // Create hidden containers for backward compat
    const hiddenHost = document.createElement('div');
    hiddenHost.style.display = 'none';
    hiddenHost.id = 'toolbar-hidden-containers';
    for (const name of ['game', 'edit', 'materials', 'debug', 'audio']) {
      const c = document.createElement('div');
      c.id = `toolbar-container-${name}`;
      hiddenHost.appendChild(c);
      this.contentContainers[name] = c;
    }
    document.body.appendChild(hiddenHost);

    // Create submenu containers (visible inline or in a small panel)
    for (const name of ['npc', 'vegetation']) {
      const c = document.createElement('div');
      c.className = 'toolbar-submenu-content';
      c.style.display = 'none';
      this._submenuContainers[name] = c;
      // These will be positioned inside the top sheet or inline
    }
  }

  // ---- SVG Icon Helpers ----

  _icon(name) {
    const icons = {
      back: '<svg viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"></polyline></svg>',
      play: '<svg viewBox="0 0 24 24"><polygon points="6 3 20 12 6 21 6 3"></polygon></svg>',
      edit: '<svg viewBox="0 0 24 24"><path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path></svg>',
      settings: '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>',
      cube: '<svg viewBox="0 0 24 24"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>',
      npc: '<svg viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>',
      material: '<svg viewBox="0 0 24 24"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"></path></svg>',
      vegetation: '<svg viewBox="0 0 24 24"><path d="M12 22V8"></path><path d="M5 12H2a10 10 0 0 0 10-10v0a10 10 0 0 0 10 10h-3"></path><path d="M5 20a5 5 0 0 1 5-5h4a5 5 0 0 1 5 5"></path></svg>',
      add: '<svg viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>',
      move: '<svg viewBox="0 0 24 24"><polyline points="5 9 2 12 5 15"></polyline><polyline points="9 5 12 2 15 5"></polyline><polyline points="15 19 12 22 9 19"></polyline><polyline points="19 9 22 12 19 15"></polyline><line x1="2" y1="12" x2="22" y2="12"></line><line x1="12" y1="2" x2="12" y2="22"></line></svg>',
      extend: '<svg viewBox="0 0 24 24"><polyline points="15 3 21 3 21 9"></polyline><polyline points="9 21 3 21 3 15"></polyline><line x1="21" y1="3" x2="14" y2="10"></line><line x1="3" y1="21" x2="10" y2="14"></line></svg>',
      swatch: '<svg viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect></svg>',
      inventory: '<svg viewBox="0 0 24 24"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect><path d="M16 7V5a4 4 0 0 0-8 0v2"></path></svg>',
      character: '<svg viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>',
      map: '<svg viewBox="0 0 24 24"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"></polygon><line x1="8" y1="2" x2="8" y2="18"></line><line x1="16" y1="6" x2="16" y2="22"></line></svg>',
      list: '<svg viewBox="0 0 24 24"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>',
      check: '<svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"></polyline></svg>',
      dungeon: '<svg viewBox="0 0 24 24"><path d="M3 21V3h18v18H3zM7 3v18M17 3v18M3 12h18M3 7h4M17 7h4M3 17h4M17 17h4M10 3v4M10 17v4"></path></svg>',
      sword: '<svg viewBox="0 0 24 24"><path d="M14.5 17.5L3 6V3h3l11.5 11.5"></path><path d="M13 19l6-6"></path><path d="M16 16l4 4"></path><path d="M19 21l2-2"></path><path d="M14.5 6.5L18 3h3v3l-3.5 3.5"></path></svg>',
      undo: '<svg viewBox="0 0 24 24"><path d="M3 10h10a5 5 0 0 1 5 5v0a5 5 0 0 1-5 5H8"></path><polyline points="7 14 3 10 7 6"></polyline></svg>',
      redo: '<svg viewBox="0 0 24 24"><path d="M21 10H11a5 5 0 0 0-5 5v0a5 5 0 0 0 5 5h5"></path><polyline points="17 14 21 10 17 6"></polyline></svg>',
      scroll: '<svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><path d="M14 2v6h6"></path><line x1="8" y1="13" x2="16" y2="13"></line><line x1="8" y1="17" x2="16" y2="17"></line></svg>',
    };
    return icons[name] || '';
  }

  _btn(iconName, cls = '', title = '') {
    const btn = document.createElement('button');
    btn.className = `toolbar-btn ${cls}`.trim();
    btn.innerHTML = this._icon(iconName);
    if (title) btn.title = title;
    return btn;
  }

  _sep() {
    const sep = document.createElement('div');
    sep.className = 'toolbar-separator';
    return sep;
  }

  // ---- Rendering ----

  _render() {
    this.contentEl.innerHTML = '';
    const level = this._currentLevel();

    switch (level) {
      case 'root':
        this._renderRoot();
        break;
      case 'play':
        this._renderPlay();
        break;
      case 'edit':
        this._renderEdit();
        break;
      case 'cube':
        this._renderCube();
        break;
      case 'npc':
        this._renderNPC();
        break;
      case 'material':
        this._renderMaterial();
        break;
      case 'vegetation':
        this._renderVegetation();
        break;
    }
  }

  _currentLevel() {
    return this._navStack[this._navStack.length - 1];
  }

  _backBtn() {
    const btn = this._btn('back', 'toolbar-back', 'Back');
    btn.addEventListener('click', () => this._goBack());
    return btn;
  }

  _renderRoot() {
    // Play button
    const playBtn = this._btn('play', 'toolbar-mode-play', 'Play');
    playBtn.addEventListener('click', () => this._navigate('play'));

    // Edit button
    const editBtn = this._btn('edit', 'toolbar-mode-edit', 'Edit');
    editBtn.addEventListener('click', () => this._navigate('edit'));

    // Settings button — toggles sidebar
    const settingsBtn = this._btn('settings', 'toolbar-settings', 'Settings');
    settingsBtn.addEventListener('click', () => {
      if (this.settingsPanel) {
        if (this.settingsPanel.isOpen()) this.settingsPanel.close();
        else this.settingsPanel.open();
      }
    });

    this.contentEl.appendChild(playBtn);
    this.contentEl.appendChild(this._sep());
    this.contentEl.appendChild(editBtn);
    this.contentEl.appendChild(this._sep());
    this.contentEl.appendChild(settingsBtn);
  }

  _renderPlay() {
    this.contentEl.appendChild(this._backBtn());
    this.contentEl.appendChild(this._sep());

    if (window.game?._dungeonMode) {
      // In dungeon: show objectives toggle instead of dungeon entry
      const objBtn = this._btn('scroll', '', 'Objectives (O)');
      objBtn.addEventListener('click', () => {
        if (this.windowManager) this.windowManager.toggle('objectives');
      });
      this.contentEl.appendChild(objBtn);
    } else {
      // Normal mode: dungeon entry button
      const dungeonBtn = this._btn('dungeon', '', 'Enter Dungeon');
      dungeonBtn.addEventListener('click', () => {
        if (window.game && window.game.showDungeonConfig) {
          window.game.showDungeonConfig();
        }
      });
      this.contentEl.appendChild(dungeonBtn);
    }
    this.contentEl.appendChild(this._sep());

    // Play-mode windows
    const invBtn = this._btn('inventory', '', 'Inventory (I)');
    invBtn.addEventListener('click', () => {
      if (this.windowManager) this.windowManager.toggle('inventory');
    });

    const charBtn = this._btn('character', '', 'Character (C)');
    charBtn.addEventListener('click', () => {
      if (this.windowManager) this.windowManager.toggle('character');
    });

    const mapBtn = this._btn('map', '', 'Map (M)');
    mapBtn.addEventListener('click', () => {
      if (this.windowManager) this.windowManager.toggle('map');
    });

    this.contentEl.appendChild(invBtn);
    this.contentEl.appendChild(charBtn);
    this.contentEl.appendChild(mapBtn);
  }

  _renderEdit() {
    this.contentEl.appendChild(this._backBtn());
    this.contentEl.appendChild(this._sep());

    const cubeBtn = this._btn('cube', '', 'Cube Editor');
    cubeBtn.addEventListener('click', () => this._navigate('cube'));

    const npcBtn = this._btn('npc', '', 'NPC Placement');
    npcBtn.addEventListener('click', () => this._navigate('npc'));

    const matBtn = this._btn('material', '', 'Material Editor');
    matBtn.addEventListener('click', () => this._navigate('material'));

    const vegBtn = this._btn('vegetation', '', 'Vegetation');
    vegBtn.addEventListener('click', () => this._navigate('vegetation'));

    this.contentEl.appendChild(cubeBtn);
    this.contentEl.appendChild(npcBtn);
    this.contentEl.appendChild(matBtn);
    this.contentEl.appendChild(vegBtn);

    this._appendUndoRedoButtons();
  }

  _renderCube() {
    this.contentEl.appendChild(this._backBtn());
    this.contentEl.appendChild(this._sep());

    // Add / Move / Extend tool buttons
    const addBtn = this._btn('add', '', 'Add Cuboid (1)');
    const moveBtn = this._btn('move', '', 'Move Cuboid (2)');
    const extendBtn = this._btn('extend', '', 'Extend Face (3)');

    this._toolButtons = { add: addBtn, move: moveBtn, extend: extendBtn };

    addBtn.addEventListener('click', () => {
      if (this.voxelEditor) this.voxelEditor.setTool('add');
      this._highlightTool('add');
    });
    moveBtn.addEventListener('click', () => {
      if (this.voxelEditor) this.voxelEditor.setTool('move');
      this._highlightTool('move');
    });
    extendBtn.addEventListener('click', () => {
      if (this.voxelEditor) this.voxelEditor.setTool('extend');
      this._highlightTool('extend');
    });

    // Highlight current tool
    this._highlightTool(this._activeTool);

    this.contentEl.appendChild(addBtn);
    this.contentEl.appendChild(moveBtn);
    this.contentEl.appendChild(extendBtn);

    // Swatch button (opens presets sheet)
    this.contentEl.appendChild(this._sep());
    const swatchBtn = this._btn('swatch', '', 'Material Presets');
    this._swatchBtn = swatchBtn;
    this._updateSwatchColor();
    swatchBtn.addEventListener('click', () => {
      if (this.presetsSheet) {
        if (this.presetsSheet.isOpen()) {
          this.presetsSheet.close();
        } else {
          this.presetsSheet.open('Material Presets');
        }
      }
    });
    this.contentEl.appendChild(swatchBtn);

    this._appendUndoRedoButtons();
  }

  _renderNPC() {
    this.contentEl.appendChild(this._backBtn());
    this.contentEl.appendChild(this._sep());

    if (this.npcTool) {
      // Character selector
      const charSelect = document.createElement('select');
      charSelect.className = 'toolbar-select';
      charSelect.title = 'Character';

      // Add LPC Custom option first if an LPC config exists
      if (this.npcTool.isLPCSelected()) {
        const lpcOpt = document.createElement('option');
        lpcOpt.value = '__lpc_custom__';
        lpcOpt.textContent = `✦ ${this.npcTool.getLPCDisplayName() || 'LPC Custom'}`;
        lpcOpt.selected = true;
        charSelect.appendChild(lpcOpt);
      }

      const names = this.npcTool.getCharacterNames();
      const currentChar = this.npcTool.getCharacter();
      names.forEach(name => {
        const opt = document.createElement('option');
        opt.value = name;
        opt.textContent = name.charAt(0).toUpperCase() + name.slice(1);
        if (!this.npcTool.isLPCSelected() && name === currentChar) opt.selected = true;
        charSelect.appendChild(opt);
      });
      charSelect.addEventListener('change', () => {
        this.npcTool.setCharacter(charSelect.value);
      });
      this.contentEl.appendChild(charSelect);

      // Behavior toggle (Wander / Patrol)
      const toggleGroup = document.createElement('div');
      toggleGroup.className = 'toolbar-toggle-group';
      const currentBehavior = this.npcTool.getBehavior();

      const wanderBtn = document.createElement('button');
      wanderBtn.className = 'toolbar-toggle-btn' + (currentBehavior === 'wander' ? ' active' : '');
      wanderBtn.textContent = 'W';
      wanderBtn.title = 'Wander';
      wanderBtn.addEventListener('click', () => {
        this.npcTool.setBehavior('wander');
        wanderBtn.classList.add('active');
        patrolBtn.classList.remove('active');
      });

      const patrolBtn = document.createElement('button');
      patrolBtn.className = 'toolbar-toggle-btn' + (currentBehavior === 'patrol' ? ' active' : '');
      patrolBtn.textContent = 'P';
      patrolBtn.title = 'Patrol';
      patrolBtn.addEventListener('click', () => {
        this.npcTool.setBehavior('patrol');
        patrolBtn.classList.add('active');
        wanderBtn.classList.remove('active');
      });

      toggleGroup.appendChild(wanderBtn);
      toggleGroup.appendChild(patrolBtn);
      this.contentEl.appendChild(toggleGroup);

      // Aggressive toggle — small inline button matching W/P size
      const aggroBtn = document.createElement('button');
      aggroBtn.className = 'toolbar-aggro-toggle' + (this.npcTool.getAggressive() ? ' active' : '');
      aggroBtn.innerHTML = this._icon('sword');
      aggroBtn.title = 'Aggressive (hostile to player)';
      aggroBtn.addEventListener('click', () => {
        const newState = !this.npcTool.getAggressive();
        this.npcTool.setAggressive(newState);
        aggroBtn.classList.toggle('active', newState);
      });
      this.contentEl.appendChild(aggroBtn);

      // LPC NPC Creator button (green)
      const lpcBtn = document.createElement('button');
      lpcBtn.className = 'toolbar-aggro-toggle toolbar-lpc-btn';
      lpcBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="8" r="4"/><path d="M5 20c0-4 3.5-7 7-7s7 3 7 7"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="16" y1="11" x2="22" y2="11"/></svg>';
      lpcBtn.title = 'Create LPC NPC (custom appearance & gear)';
      lpcBtn.style.cssText = 'color: #4cdf7a; border-color: rgba(76,223,122,0.4);';
      lpcBtn.addEventListener('click', () => {
        this.npcTool.openLPCCreator();
      });
      this.contentEl.appendChild(lpcBtn);

      // Patrol Done button + status (visible only during patrol placement)
      const doneBtn = this._btn('check', 'toolbar-patrol-done', 'Finish Patrol (place NPC)');
      doneBtn.addEventListener('click', () => {
        if (this.npcTool) this.npcTool.finishPatrol();
      });
      this._patrolDoneBtn = doneBtn;
      this.contentEl.appendChild(doneBtn);

      // Update patrol UI visibility
      this._updatePatrolUI();

      this.contentEl.appendChild(this._sep());

      // List/settings button — toggles tool sheet
      const listBtn = this._btn('list', '', 'NPC List & Settings');
      listBtn.addEventListener('click', () => {
        if (this.toolSheet) {
          if (this.toolSheet.isOpen()) {
            this.toolSheet.close();
          } else {
            this._toggleToolSheetContent('npc');
            this.toolSheet.open('NPC Settings');
          }
        }
      });
      this.contentEl.appendChild(listBtn);
    } else {
      const label = document.createElement('span');
      label.style.cssText = 'font-size: 12px; color: var(--text-secondary); padding: 0 8px; white-space: nowrap;';
      label.textContent = 'Click to place NPC';
      this.contentEl.appendChild(label);
    }

    this._appendUndoRedoButtons();
  }

  _updatePatrolUI() {
    if (!this.npcTool || !this._patrolDoneBtn) return;

    const isPatrol = this.npcTool.getBehavior() === 'patrol';
    const placing = this.npcTool.isPlacing();
    const count = this.npcTool.getPatrolPointCount();
    const showPatrolUI = isPatrol && placing;

    this._patrolDoneBtn.style.display = showPatrolUI ? '' : 'none';

    if (showPatrolUI) {
      this._patrolDoneBtn.disabled = count < 2;
      this._patrolDoneBtn.style.opacity = count < 2 ? '0.4' : '1';
    }
  }

  _renderMaterial() {
    this.contentEl.appendChild(this._backBtn());
    this.contentEl.appendChild(this._sep());

    const label = document.createElement('span');
    label.className = 'toolbar-label';
    label.textContent = 'Click a face to edit';
    label.style.cssText = 'font-size: 12px; color: var(--text-secondary); padding: 0 8px; white-space: nowrap;';
    this.contentEl.appendChild(label);

    // Material editor swatch button — click to reopen the editor sheet
    this.contentEl.appendChild(this._sep());
    const matBtn = this._btn('material', '', 'Material Editor');
    matBtn.addEventListener('click', () => {
      if (this.materialsSheet) {
        if (this.materialsSheet.isOpen()) {
          this.materialsSheet.close();
        } else {
          this.materialsSheet.open('Material Editor');
        }
      }
    });
    this.contentEl.appendChild(matBtn);

    this._appendUndoRedoButtons();
  }

  _renderVegetation() {
    this.contentEl.appendChild(this._backBtn());
    this.contentEl.appendChild(this._sep());

    const label = document.createElement('span');
    label.className = 'toolbar-label';
    label.textContent = 'Click surface to plant';
    label.style.cssText = 'font-size: 12px; color: var(--text-secondary); padding: 0 8px; white-space: nowrap;';
    this.contentEl.appendChild(label);

    // Vegetation swatch button (mirrors cuboid swatch pattern) — click to open/close picker
    this.contentEl.appendChild(this._sep());
    const vegSwatchBtn = this._btn('vegetation', '', 'Change Vegetation');
    this._vegSwatchBtn = vegSwatchBtn;
    this._updateVegSwatch();
    vegSwatchBtn.addEventListener('click', () => {
      if (this.toolSheet) {
        if (this.toolSheet.isOpen()) {
          this.toolSheet.close();
        } else {
          this._toggleToolSheetContent('vegetation');
          this.toolSheet.open('Vegetation');
        }
      }
    });
    this.contentEl.appendChild(vegSwatchBtn);

    this._appendUndoRedoButtons();
  }

  // ---- Navigation ----

  _navigate(level) {
    this._navStack.push(level);
    window.game?.audioSystem?.audio?.playUiClick();
    this._render();
    this._onNavigationChange();
  }

  _goBack() {
    if (this._navStack.length <= 1) return;
    const leaving = this._navStack.pop();
    window.game?.audioSystem?.audio?.playUiClick();
    this._render();
    this._onNavigationChange(leaving);
  }

  /**
   * Called after every navigation change to update game state
   * @param {string} [leavingLevel] - the level we just left (for cleanup)
   */
  _onNavigationChange(leavingLevel) {
    const level = this._currentLevel();
    const inEditTree = this._navStack.includes('edit');
    const inPlayTree = this._navStack.includes('play');

    // Determine which "tab" is active for backward compat
    let newTab;
    if (level === 'material') {
      newTab = 'materials';
    } else if (inEditTree) {
      newTab = 'edit';
    } else if (inPlayTree) {
      newTab = 'game';
    } else {
      newTab = 'game'; // root defaults to game mode
    }

    // Toggle material-editor-mode body class
    document.body.classList.toggle('material-editor-mode', level === 'material');

    // Only dispatch if tab actually changed
    if (newTab !== this.activeTab) {
      this.activeTab = newTab;
      window.dispatchEvent(new CustomEvent('sidebar-tab-change', {
        detail: { tab: newTab }
      }));
    }

    // Dispatch submenu change event
    window.dispatchEvent(new CustomEvent('toolbar-submenu-change', {
      detail: { level, navStack: [...this._navStack] }
    }));

    // Auto-activate add tool when entering cube submenu
    if (level === 'cube' && !this._activeTool) {
      if (this.voxelEditor) this.voxelEditor.setTool('add');
      this._activeTool = 'add';
    }

    // Cleanup when leaving cube submenu
    if (leavingLevel === 'cube') {
      if (this.voxelEditor) this.voxelEditor.setTool(null);
      this._activeTool = null;
      if (this.presetsSheet) this.presetsSheet.close();
    }

    // Close materials sheet when leaving material submenu
    if (leavingLevel === 'material') {
      if (this.materialsSheet) this.materialsSheet.close();
    }

    // Open tool sheet for vegetation submenu (NPC uses toolbar inline + manual list button)
    if (level === 'vegetation' && this.toolSheet) {
      this._toggleToolSheetContent(level);
      this.toolSheet.open('Vegetation');
    }

    // Close tool sheet when leaving NPC / vegetation
    if ((leavingLevel === 'npc' || leavingLevel === 'vegetation') && this.toolSheet) {
      this.toolSheet.close();
    }
  }

  _highlightTool(toolName) {
    this._activeTool = toolName;
    if (this._toolButtons) {
      Object.entries(this._toolButtons).forEach(([name, btn]) => {
        btn.classList.toggle('active', name === toolName);
      });
    }
  }

  _updateSwatchColor() {
    if (!this._swatchBtn) return;
    // Apply stored swatch preview
    if (this._swatchTexture) {
      this._swatchBtn.innerHTML = '';
      const preview = document.createElement('div');
      preview.className = 'toolbar-swatch-preview';
      preview.style.backgroundImage = `url('${this._swatchTexture}')`;
      preview.style.backgroundSize = 'cover';
      preview.style.backgroundPosition = 'center';
      this._swatchBtn.appendChild(preview);
    } else if (this._swatchColor) {
      this._swatchBtn.innerHTML = '';
      const preview = document.createElement('div');
      preview.className = 'toolbar-swatch-preview';
      preview.style.backgroundColor = this._swatchColor;
      this._swatchBtn.appendChild(preview);
    }
  }

  /**
   * Show/hide content within the shared tool sheet based on active submenu
   * NPC and vegetation tools both render into the same sheet container
   */
  _toggleToolSheetContent(activeLevel) {
    if (!this.toolSheet) return;
    const container = this.toolSheet.getContentContainer();
    // Toggle visibility of gui-sections: NPC section has id="npcSection"
    const sections = container.querySelectorAll('.gui-section');
    sections.forEach(section => {
      if (activeLevel === 'npc') {
        // Show NPC sections (id starts with 'npc'), hide others
        section.style.display = section.id && section.id.startsWith('npc') ? '' : 'none';
      } else if (activeLevel === 'vegetation') {
        // Show vegetation/impostor sections, hide NPC sections
        section.style.display = section.id && section.id.startsWith('npc') ? 'none' : '';
      }
    });
  }

  // ---- Public API ----

  /**
   * Set the VoxelEditor reference (called after VoxelEditor is created)
   */
  setVoxelEditor(editor) {
    this.voxelEditor = editor;
  }

  /**
   * Set the NPCTool reference (called after NPCTool is created)
   */
  setNPCTool(npcTool) {
    this.npcTool = npcTool;
  }

  /**
   * Set the UndoManager reference and wire change callback.
   */
  setUndoManager(undoManager) {
    this._undoManager = undoManager;
    undoManager.setOnChange(() => this._updateUndoButtons());
  }

  /**
   * Append undo/redo buttons to the current toolbar content.
   * @private
   */
  _appendUndoRedoButtons() {
    this.contentEl.appendChild(this._sep());

    const undoBtn = this._btn('undo', 'toolbar-undo-btn', 'Undo (Ctrl+Z)');
    const redoBtn = this._btn('redo', 'toolbar-undo-btn', 'Redo (Ctrl+Y)');
    this._undoBtn = undoBtn;
    this._redoBtn = redoBtn;

    undoBtn.addEventListener('click', () => {
      if (this._undoManager) this._undoManager.undo();
    });
    redoBtn.addEventListener('click', () => {
      if (this._undoManager) this._undoManager.redo();
    });

    const stack = document.createElement('div');
    stack.className = 'toolbar-undo-stack';
    stack.appendChild(undoBtn);
    stack.appendChild(redoBtn);
    this.contentEl.appendChild(stack);
    this._updateUndoButtons();
  }

  /**
   * Update disabled state of undo/redo buttons.
   * @private
   */
  _updateUndoButtons() {
    if (this._undoBtn) {
      const canUndo = this._undoManager && this._undoManager.canUndo();
      this._undoBtn.disabled = !canUndo;
      this._undoBtn.style.opacity = canUndo ? '1' : '0.3';
    }
    if (this._redoBtn) {
      const canRedo = this._undoManager && this._undoManager.canRedo();
      this._redoBtn.disabled = !canRedo;
      this._redoBtn.style.opacity = canRedo ? '1' : '0.3';
    }
  }

  /**
   * Update the vegetation preview thumbnail in the toolbar
   * @param {string|null} thumbnailUrl - URL of the selected texture thumbnail
   */
  updateVegPreview(thumbnailUrl) {
    this._vegPreviewThumb = thumbnailUrl || null;
    this._updateVegSwatch();
  }

  /**
   * Apply the vegetation thumbnail to the swatch button (mirrors _updateSwatchColor)
   * @private
   */
  _updateVegSwatch() {
    if (!this._vegSwatchBtn) return;
    if (this._vegPreviewThumb) {
      this._vegSwatchBtn.innerHTML = '';
      const preview = document.createElement('div');
      preview.className = 'toolbar-swatch-preview';
      preview.style.backgroundImage = `url('${this._vegPreviewThumb}')`;
      preview.style.backgroundSize = 'contain';
      preview.style.backgroundPosition = 'center';
      preview.style.backgroundRepeat = 'no-repeat';
      this._vegSwatchBtn.appendChild(preview);
    }
  }

  /**
   * Set the active tool highlight (called by VoxelEditor)
   * @param {string|null} toolName
   */
  setActiveTool(toolName) {
    this._activeTool = toolName;
    if (this._currentLevel() === 'cube') {
      this._highlightTool(toolName);
    }
  }

  /**
   * Update the swatch button preview
   * @param {string|null} color - CSS color string, or null
   * @param {string|null} texturePath - Thumbnail path, or null
   */
  updateSwatchPreview(color, texturePath) {
    this._swatchColor = color || null;
    this._swatchTexture = texturePath || null;
    this._updateSwatchColor();
  }

  /**
   * Get a container for a specific submenu's inline content
   * @param {string} name - 'npc' or 'vegetation'
   * @returns {HTMLElement}
   */
  getSubmenuContainer(name) {
    return this._submenuContainers[name] || null;
  }

  /**
   * Get the current navigation level
   * @returns {string}
   */
  getCurrentLevel() {
    return this._currentLevel();
  }

  /**
   * Get the full navigation stack
   * @returns {string[]}
   */
  getNavStack() {
    return [...this._navStack];
  }

  // ---- SidebarManager Backward Compatibility API ----

  /**
   * Get the content container for a specific tab
   * Maps old tab names to new containers:
   * - 'game' → hidden container (world controls go to settings now)
   * - 'edit' → hidden container (presets go to presetsSheet)
   * - 'materials' → materialsSheet content container
   * - 'debug' → settingsPanel debug tab container
   * - 'audio' → settingsPanel audio tab container
   */
  getContentContainer(tabName) {
    switch (tabName) {
      case 'materials':
        return this.materialsSheet
          ? this.materialsSheet.getContentContainer()
          : this.contentContainers.materials;
      case 'debug':
        return this.settingsPanel
          ? this.settingsPanel.getTabContainer('debug')
          : this.contentContainers.debug;
      case 'audio':
        return this.settingsPanel
          ? this.settingsPanel.getTabContainer('audio')
          : this.contentContainers.audio;
      case 'game':
        // World controls and lighting now go to settings display tab
        return this.settingsPanel
          ? this.settingsPanel.getTabContainer('display')
          : this.contentContainers.game;
      case 'edit':
        return this.presetsSheet
          ? this.presetsSheet.getContentContainer()
          : this.contentContainers.edit;
      default:
        return this.contentContainers[tabName] || document.createElement('div');
    }
  }

  /**
   * Check if a tab is active
   * @param {string} tabName - Tab name to check
   * @returns {boolean}
   */
  isTabActive(tabName) {
    return this.activeTab === tabName;
  }

  /**
   * Programmatically switch to a tab
   * @param {string} tabName - Tab to switch to
   */
  switchToTab(tabName) {
    this.setActiveTab(tabName);
  }

  /**
   * Set the active tab (backward compat)
   * Maps old tab names to new navigation
   */
  setActiveTab(tabName) {
    switch (tabName) {
      case 'game':
        // Navigate to play mode (or root)
        this._navStack = ['root', 'play'];
        break;
      case 'edit':
        this._navStack = ['root', 'edit', 'cube'];
        break;
      case 'materials':
        this._navStack = ['root', 'edit', 'material'];
        break;
      case 'debug':
        if (this.settingsPanel) this.settingsPanel.open('debug');
        return;
      case 'audio':
        if (this.settingsPanel) this.settingsPanel.open('audio');
        return;
    }

    const newTab = tabName === 'game' ? 'game' : (tabName === 'materials' ? 'materials' : 'edit');
    this.activeTab = newTab;
    this._render();

    // Toggle material-editor-mode
    document.body.classList.toggle('material-editor-mode', tabName === 'materials');

    window.dispatchEvent(new CustomEvent('sidebar-tab-change', {
      detail: { tab: newTab }
    }));

    // Also dispatch submenu change for toolbar-aware listeners
    window.dispatchEvent(new CustomEvent('toolbar-submenu-change', {
      detail: { level: this._currentLevel(), navStack: [...this._navStack] }
    }));
  }

  destroy() {
    this.el.remove();
    const hidden = document.getElementById('toolbar-hidden-containers');
    if (hidden) hidden.remove();
  }
}
